/**
 * MockParticipantClient - Manages a single mock participant's socket connection
 *
 * Handles:
 * - Creating anonymous user via API
 * - Establishing socket connection with token auth
 * - Joining session
 * - Changing role and casting votes
 * - Disconnecting and cleanup
 *
 * Uses the same socket.io patterns and contracts as pokerly-ws.ts
 */

import { PokerlyWsClient } from '@/services/pokerly-ws/pokerly-ws-client.js';
import { ref, type Ref } from 'vue';
import { ClientGameSessionEvent, ServerGameSessionEvent } from '@pokerly/contracts';
import type { RequiredUserVote, UserRole } from '@pokerly/contracts/events/common';
import { env } from '@/config/env.ts';
import type { MockParticipantClient, MockParticipantStatus } from '../types.ts';

const SOCKET_OPERATION_TIMEOUT_MS = 10000; // Same as pokerly-ws.ts

export function createMockParticipantClient(id: string): MockParticipantClient {
    const status: Ref<MockParticipantStatus> = ref('idle');
    const role: Ref<UserRole> = ref('voter');
    const currentVote: Ref<RequiredUserVote | null> = ref(null);
    let client: PokerlyWsClient | null = null;
    const profile = ref<{
        id: string;
        name: string;
        email: string;
        profilePictureId?: string;
    } | null>(null);

    // Session state needed for vote/role payloads
    let sessionId: string | null = null;
    let roundId: string | null = null;
    let roundReplayId: string | null = null;

    /**
     * Connects the mock participant to the session
     */
    async function connect(sessionRoom: string): Promise<void> {
        if (status.value !== 'idle') {
            throw new Error(`Cannot connect: status is ${status.value}`);
        }

        status.value = 'connecting';

        try {
            // 1. Create anonymous user and get token
            const response = await fetch(`${env.VITE_API_ROOT}/auth/dev/anonymous-token`, {
                method: 'POST',
            });

            if (!response.ok) {
                const err = new Error(`Failed to create anonymous user: ${response.statusText}`);
                status.value = 'error';
                throw err;
            }

            const { token, profile: _profile } = await response.json();
            profile.value = _profile;
            // 2. Create socket with token auth (same config pattern as PokerlyWs)
            client = new PokerlyWsClient({ token });

            // 3. Wait for connection, then join session using contracts
            await new Promise<void>((resolve, reject) => {
                const connectionTimeout = setTimeout(() => {
                    reject(new Error('Socket connection timeout'));
                }, SOCKET_OPERATION_TIMEOUT_MS);

                client?.socket!.on('connect', async () => {
                    clearTimeout(connectionTimeout);
                    try {
                        // Use the same event and pattern as PokerlyWs.connect()
                        const joinResponse = await client!.emitWithAck(
                            ClientGameSessionEvent.SESSION_JOIN,
                            {
                                room: sessionRoom,
                            },
                            { description: 'Session join' },
                        );

                        if (joinResponse?.sessionId) {
                            // Store session state for vote/role payloads
                            sessionId = joinResponse.sessionId;
                            roundId = joinResponse.roundId;
                            roundReplayId = joinResponse.roundReplayId;

                            status.value = 'connected';
                            resolve();
                        } else {
                            status.value = 'error';
                            reject(new Error('Failed to join session'));
                        }
                    } catch (err) {
                        status.value = 'error';
                        reject(err);
                    }
                });

                client?.socket!.on('connect_error', (error: Error) => {
                    clearTimeout(connectionTimeout);
                    console.error(`MockParticipant ${id} connection error:`, error);
                    status.value = 'error';
                    reject(error);
                });
            });

            // 4. Handle ongoing socket events
            client!.socket.on('disconnect', () => {
                if (status.value === 'connected') {
                    status.value = 'error';
                }
            });

            client!.socket.on('error', (error: Error) => {
                console.error(`MockParticipant ${id} socket error:`, error);
                status.value = 'error';
            });

            // 5. Listen for round lifecycle events to keep IDs up to date
            client!.socket.on(
                ServerGameSessionEvent.ROUND_CLOSED,
                (payload: { roundId: string; roundReplayId: string }) => {
                    roundId = payload.roundId;
                    roundReplayId = payload.roundReplayId;
                },
            );

            client!.socket.on(
                ServerGameSessionEvent.ROUND_FINISHED,
                (payload: { roundId: string; roundReplayId: string }) => {
                    roundId = payload.roundId;
                    roundReplayId = payload.roundReplayId;
                    currentVote.value = null;
                },
            );

            client!.socket.on(
                ServerGameSessionEvent.ROUND_REPLAY,
                (payload: { roundId: string; roundReplayId: string }) => {
                    roundId = payload.roundId;
                    roundReplayId = payload.roundReplayId;
                    currentVote.value = null;
                },
            );
        } catch (error) {
            console.error(`Failed to connect mock participant ${id}:`, error);
            status.value = 'error';
            throw error;
        }
    }

    /**
     * Changes the participant's role (voter/observer)
     */
    async function setRole(newRole: UserRole): Promise<void> {
        if (!client || !sessionId || !roundId || !roundReplayId) return;
        if (status.value !== 'connected') return;

        await client.emitWithAck(
            ClientGameSessionEvent.USER_ROLE_UPDATE,
            { sessionId, roundId, roundReplayId, role: newRole },
            { description: 'Mock: set role' },
        );
        role.value = newRole;
        if (newRole === 'observer') {
            currentVote.value = null;
        }
    }

    /**
     * Casts a vote (only if role is voter)
     */
    async function vote(value: RequiredUserVote): Promise<void> {
        if (!client || !sessionId || !roundId || !roundReplayId) return;
        if (status.value !== 'connected') return;
        if (role.value !== 'voter') return;

        await client.emitWithAck(
            ClientGameSessionEvent.USER_VOTE,
            { sessionId, roundId, roundReplayId, vote: value },
            { description: 'Mock: cast vote' },
        );
        currentVote.value = value;
    }

    /**
     * Clears the current vote (local-only reset)
     */
    function clearVote(): void {
        currentVote.value = null;
    }

    /**
     * Disconnects the mock participant
     */
    function disconnect(): void {
        client?.disconnect();
        status.value = 'idle';
        sessionId = null;
        roundId = null;
        roundReplayId = null;
    }

    return {
        id,
        profile,
        status,
        role,
        currentVote,
        connect,
        disconnect,
        setRole,
        vote,
        clearVote,
    };
}
